import log, { Logger, LogLevelNumbers } from 'loglevel';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { ENV } from '../config/env';

const connectionString = ENV.LOGGER_INSIGHT_CONNECTION_STRING;
const isProduction = process.env.NODE_ENV === 'production';

const nrLicenseKey = ENV.NEWRELIC_LICENSE_KEY;
const nrAccountId = ENV.NEWRELIC_ACCOUNT_ID;
const nrTrustKey = ENV.NEWRELIC_TRUST_KEY;
const nrAppId = ENV.NEWRELIC_APP_ID;

// loglevel : TRACE=0, DEBUG=1, INFO=2, WARN=3, ERROR=4, SILENT=5
// App Insights : Verbose=0, Information=1, Warning=2, Error=3, Critical=4
const toSeverityLevel = (logLevel: LogLevelNumbers): SeverityLevel => {
  if (logLevel <= 1) return SeverityLevel.Verbose; // TRACE, DEBUG
  if (logLevel === 2) return SeverityLevel.Information; // INFO
  if (logLevel === 3) return SeverityLevel.Warning; // WARN
  return SeverityLevel.Error; // ERROR
};

let _appInsights: ApplicationInsights | null = null;

function loadAppInsights(): ApplicationInsights | null {
  if (!connectionString) {
    return null;
  }

  if (_appInsights) {
    return _appInsights;
  }

  try {
    _appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableDebug: !isProduction,
      },
    });

    _appInsights.loadAppInsights();
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
    return null;
  }

  return _appInsights;
}

const appInsights = loadAppInsights();

function loadNewRelic(): void {
  if (!nrLicenseKey || !nrAccountId || !nrTrustKey || !nrAppId) {
    return;
  }

  // Use a same-domain proxy path so ad blockers don't intercept New Relic traffic
  // Must be host-only (no scheme) — the agent prepends its own protocol when building the beacon URL
  const nrBeacon = `${window.location.host}/nr-data`;

  try {
    new BrowserAgent({
      init: {
        browser_consent_mode: { enabled: false },
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        performance: { capture_measures: true },
        ajax: { deny_list: ['bam.nr-data.net'] },
      },
      info: {
        beacon: nrBeacon,
        errorBeacon: nrBeacon,
        licenseKey: nrLicenseKey,
        applicationID: nrAppId,
        sa: 1,
      },
      loader_config: {
        accountID: nrAccountId,
        trustKey: nrTrustKey,
        agentID: nrAppId,
        licenseKey: nrLicenseKey,
        applicationID: nrAppId,
      },
    });
  } catch (error) {
    console.error('Failed to initialize New Relic Browser agent:', error);
  }
}

loadNewRelic();

// Configuration du niveau de log selon l'environnement
if (isProduction) {
  log.setLevel('info');
} else {
  log.setLevel('debug');
}

// Surcharge pour envoyer les logs à Azure
const originalFactory = log.methodFactory;
log.methodFactory = function (methodName, logLevel, loggerName) {
  const rawMethod = originalFactory(methodName, logLevel, loggerName);
  return function (...message: unknown[]) {
    rawMethod(...message);

    appInsights?.trackTrace({
      message: `[${methodName.toUpperCase()}] ${message.map(m => (typeof m === 'object' ? JSON.stringify(m, null, 2) : m)).join(' ')}`,
      severityLevel: toSeverityLevel(logLevel),
    });
  };
};

log.setLevel(log.getLevel());

const createLogger = (namespace: string): Logger => log.getLogger(namespace);

export default createLogger;
